import React, { useEffect, useState } from "react";
import BadgeDisplay, { ProfileBadge } from "./BadgeDisplay";
import {
  Award,
  Filter,
  Trophy,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useFreelancerStore } from "@/store/freelancerStore";

interface BadgeCollectionProps {
  badges: ProfileBadge[];
  title?: string;
  emptyMessage?: string;
  className?: string;
  maxDisplay?: number; // New: Limit number of badges initially displayed
  showFilters?: boolean; // New: Show/hide filter options
  isEditing?: boolean;
  onRemove?: (badgeId: string) => void;
}

export const BadgeCollection: React.FC<BadgeCollectionProps> = ({
  badges,
  title = "Achievement Badges",
  emptyMessage = "No badges earned yet",
  className = "",
  maxDisplay = 10,
  showFilters = true,
  isEditing,
  onRemove,
}) => {
  const { profile } = useFreelancerStore();
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<string>("all");

  if (!badges || badges.length === 0) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <Award className="w-8 h-8 mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  // Filter badges based on selection
  const filteredBadges =
    filter === "all"
      ? badges
      : filter === "client"
      ? badges.filter(
          (badge) => badge.type === "client-appreciation" || badge.clientName
        )
      : filter === "skill"
      ? badges.filter((badge) => badge.type === "skill-expert")
      : filter === "rare"
      ? badges.filter(
          (badge) =>
            badge.rarity === "rare" ||
            badge.rarity === "epic" ||
            badge.rarity === "legendary"
        )
      : badges;
  // Determine which badges to display
  const displayBadges = showAll ? badges : badges.slice(0, maxDisplay);

  const hasMoreToShow = filteredBadges.length > maxDisplay;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex justify-between items-center">
        {title && (
          <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        )}

        {/* {showFilters && badges.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <span className="sr-only">Filter badges</span>
                <Filter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilter("all")}>
                All Badges
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("client")}>
                Client Appreciation
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("skill")}>
                Skill Expertise
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("rare")}>
                Rare Achievements
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )} */}
      </div>

      {/* <div className="flex flex-wrap gap-2">
        {displayBadges.map((badge) => {
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline"
                  >
                    {badge.name}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-200 rounded-md">
                  <div className="space-y-1.5">
                    <div className="font-medium">{badge.description}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div> */}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayBadges.map((badge) => (
          <div key={badge.name} className="relative">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <BadgeDisplay badge={badge} />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white p-3 shadow-lg border border-gray-200 rounded-md">
                  <div className="space-y-1.5">
                    <div className="font-medium">{badge.description}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isEditing && onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gradient-to-br from-red-300 to-rose-300 hover:from-red-400 hover:to-rose-400 text-white shadow-sm transition-all duration-200 animate-fade-in flex items-center justify-center"
                onClick={() => onRemove(badge.name)}
              >
                <Trash2 className="w-1 h-1" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {hasMoreToShow && (
        <Button
          variant="ghost"
          size="sm"
          className="text-xs w-full mt-2 text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <>
              Show Less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Show {filteredBadges.length - maxDisplay} More{" "}
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      )}
    </div>
  );
};
